import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/yup'
import * as yup from 'yup'
import {
	type AssociationMembershipCreateRequest,
	type AssociationMembershipResponse,
	type AssociationMembershipUpdateRequest,
	Gender,
} from '~~/openapi-client/gen/schemas'
import { useNuxtApp } from '#app'
import { useCommonMemberFields } from '~/composables/useCommonMemberFields'
import type { MemberTag } from '~~/types/MemberTag'

export const useAssociationMembershipForm = () => {
	const { selectedAssociation } = useAssociations()
	const { $api } = useNuxtApp()

	const member = ref<AssociationMembershipResponse>()

	const schema = toTypedSchema(yup.object({
		associationId: yup.string().required(),
		firstname: yup.string().required('Der Vorname ist erforderlich.'),
		lastname: yup.string().required('Der Nachname ist erforderlich.'),
		birthday: yup.string().optional(),
		gender: yup.mixed<Gender>().oneOf(Object.values(Gender)).optional(),
		iban: yup.string().matches(ibanRegExp, { message: 'Die IBAN ist ungültig.', excludeEmptyString: true }).optional(),
		joinDate: yup.string().optional(),
		contactInformation: yup.object({
			address: yup.object({
				street: yup.string().optional(),
				houseNumber: yup.string().optional(),
				postalCode: yup.string().optional().matches(plzRegExp, { message: 'Die PLZ ist ungültig.', excludeEmptyString: true }),
				city: yup.string().optional(),
			}),
			phone: yup.string().matches(phoneRegExp, { message: 'Die Telefonnummer ist ungültig.', excludeEmptyString: true }).optional(),
			email: yup.string().email('Die E-Mail-Adresse ist ungültig.').optional(),
		}),
		legalRepresentative: yup.string().optional(),
		tags: yup.array().of(
			yup.object({
				name: yup.string().required(),
			}),
		).optional(),
	}))

	const {
		handleSubmit,
		errors,
		meta,
		defineField,
		validateField,
		resetForm,
	} = useForm({
		validationSchema: schema,
		initialValues: {
			associationId: selectedAssociation.value?.id,
		},
	})

	const memberFields = useCommonMemberFields(defineField, validateField)
	const [legalRepresentative, legalRepresentativeAttrs] = useDebouncedField('legalRepresentative', defineField, validateField)

	const init = async (id: string) => {
		const response = await $api.associationMembershipApi.findById(id)
		member.value = response.data
		resetForm({
			values: {
				firstname: member.value.firstname,
				lastname: member.value.lastname,
				birthday: member.value.birthday ? member.value.birthday : undefined,
				gender: member.value.gender ? member.value.gender : undefined,
				contactInformation: {
					address: {
						street: member.value.contactInformationResponse?.address.street ? member.value.contactInformationResponse.address.street : undefined,
						houseNumber: member.value.contactInformationResponse?.address.houseNumber ? member.value.contactInformationResponse.address.houseNumber : undefined,
						postalCode: member.value.contactInformationResponse?.address.postalCode ? member.value.contactInformationResponse.address.postalCode : undefined,
						city: member.value.contactInformationResponse?.address.city ? member.value.contactInformationResponse.address.city : undefined,
					},
					email: member.value.contactInformationResponse?.email ? member.value.contactInformationResponse.email : undefined,
					phone: member.value.contactInformationResponse?.phone ? member.value.contactInformationResponse.phone : undefined,
				},
				iban: member.value.iban ? member.value.iban : undefined,
				joinDate: member.value.joinDate ? member.value.joinDate : undefined,
				legalRepresentative: member.value.legalRepresentative ? member.value.legalRepresentative : undefined,
				tags: member.value.tags,
			},
		})
	}

	const create = () => handleSubmit(async (formValues: AssociationMembershipCreateRequest) => {
		// Remove spaces from IBAN before sending to API
		formValues.iban = normalizeIban(formValues.iban)

		await $api.associationMembershipApi.createMembership(formValues)
	})()

	const update = (id: string) => handleSubmit(async (formValues: AssociationMembershipUpdateRequest) => {
		const userDefinedTags = (formValues.tags ?? [])
			.filter((tag: MemberTag) => tag.type !== 'FUNCTIONAL')
			.map(tag => ({ name: tag.name }))

		// Remove spaces from IBAN before sending to API
		formValues.iban = normalizeIban(formValues.iban)

		await $api.associationMembershipApi.updateMembership(id, {
			...formValues,
			tags: userDefinedTags,
		})
	})()

	return {
		memberFields: {
			...memberFields,
			legalRepresentative,
			legalRepresentativeAttrs,
		},
		resetForm,
		errors,
		meta,
		init,
		create,
		update,
	}
}
